import React, { useEffect, useState } from "react";
import { Save, RotateCcw } from "lucide-react";
import PageHeader from "@/components/admin/PageHeader";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";

const DEFAULTS = {
    rate_limit_enabled: true,
    rate_limit_messages: 20,
    rate_limit_seconds: 240,
    rate_limit_message: "Too many messages in a row",
};

export default function SettingsSecurity() {
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        adminApi.get("/api/admin/settings/security").then((r) => setData(r.data));
    }, []);

    if (!data) return <div className="p-10 text-stone-500">Loading…</div>;

    const save = async () => {
        setSaving(true);
        try {
            await adminApi.put("/api/admin/settings/security", data);
            toast.success("Security settings saved");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-10 max-w-4xl">
            <PageHeader eyebrow="Settings" title="Security" testid="page-settings-security" />

            <div className="bg-white border border-stone-200 rounded-lg p-6 mb-6">
                <div className="flex items-start justify-between mb-5">
                    <div>
                        <h2 className="font-heading text-xl text-orchid-700 mb-1">Rate limit</h2>
                        <p className="text-xs text-stone-500">Cap how many messages a single device can send to prevent abuse.</p>
                    </div>
                    <button
                        onClick={() =>
                            setData({ ...data, rate_limit_enabled: !data.rate_limit_enabled })
                        }
                        data-testid="rate-limit-toggle"
                        className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                            data.rate_limit_enabled ? "bg-orchid-700" : "bg-stone-300"
                        }`}
                    >
                        <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white ${
                                data.rate_limit_enabled ? "translate-x-5" : "translate-x-0.5"
                            }`}
                        />
                    </button>
                </div>

                {data.rate_limit_enabled && (
                    <div className="space-y-5">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-[11px] uppercase tracking-[0.2em] text-stone-500 mb-2 block">
                                    Messages
                                </label>
                                <input
                                    type="number"
                                    value={data.rate_limit_messages || 0}
                                    onChange={(e) =>
                                        setData({ ...data, rate_limit_messages: parseInt(e.target.value) || 0 })
                                    }
                                    data-testid="rate-limit-messages"
                                    className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700"
                                />
                            </div>
                            <div>
                                <label className="text-[11px] uppercase tracking-[0.2em] text-stone-500 mb-2 block">
                                    every (seconds)
                                </label>
                                <input
                                    type="number"
                                    value={data.rate_limit_seconds || 0}
                                    onChange={(e) =>
                                        setData({ ...data, rate_limit_seconds: parseInt(e.target.value) || 0 })
                                    }
                                    data-testid="rate-limit-seconds"
                                    className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="text-[11px] uppercase tracking-[0.2em] text-stone-500 mb-2 block">
                                Message to show when limit is hit
                            </label>
                            <input
                                value={data.rate_limit_message || ""}
                                onChange={(e) =>
                                    setData({ ...data, rate_limit_message: e.target.value })
                                }
                                data-testid="rate-limit-message"
                                className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700"
                            />
                        </div>
                    </div>
                )}

                <div className="flex items-center gap-2 mt-6">
                    <button
                        onClick={save}
                        disabled={saving}
                        data-testid="security-save"
                        className="inline-flex items-center gap-2 bg-orchid-700 hover:bg-orchid-600 text-white text-sm font-medium px-4 py-2 rounded-md disabled:opacity-60"
                    >
                        <Save size={14} /> {saving ? "Saving…" : "Save"}
                    </button>
                    <button
                        onClick={() => setData({ ...data, ...DEFAULTS })}
                        data-testid="security-reset"
                        className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md border border-stone-200 hover:bg-stone-50 text-stone-700"
                    >
                        <RotateCcw size={13} /> Reset
                    </button>
                </div>
            </div>
        </div>
    );
}
